// Keeps a tool's form and last run for the browser tab session.
//
// - Form fields, the last run and edits live in sessionStorage (cleared when the tab closes).
// - Uploaded images are too large for sessionStorage, so they live in IndexedDB,
//   keyed by tool and a per-tab id.
// - Signed-out users also get a "hand-off" record in IndexedDB so their draft survives
//   sign-up flows that continue in a new tab (e.g. an email verification link).
// - The tab session is tied to the login that wrote it, so a timeout, logout or
//   fresh login opens each tool on its input screen instead of the last run.

import { SUPABASE_STORAGE_KEYS } from '../lib/supabase';

const TAB_ID_KEY = 'tool-session-tab-id';
const SESSION_VERSION = 1;

const DB_NAME = 'merchantfloor-tool-drafts';
const STORE_NAME = 'drafts';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

interface SessionEnvelope<T> {
  version: number;
  // Login session that wrote this state; null when signed out.
  authSessionId: string | null;
  state: T;
}

interface StoredImages {
  files: { name: string; blob: Blob }[];
  savedAt: number;
}

interface StoredHandoff<TForm> {
  form: TForm;
  imageTabId: string | null;
  savedAt: number;
}

// Listed up front because the tools are lazy-loaded routes; clearAllToolDrafts()
// must reach every tool, not just the ones opened in this page load.
const TOOL_NAMESPACES = ['photo-studio', 'lifestyle-image', 'product-listing'] as const;
export type ToolNamespace = typeof TOOL_NAMESPACES[number];

function getTabId(): string {
  try {
    let id = sessionStorage.getItem(TAB_ID_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(TAB_ID_KEY, id);
    }
    return id;
  } catch {
    return 'default';
  }
}

// Supabase's session_id claim stays the same across token refreshes and changes on
// every sign-in, so it identifies one login. Read synchronously from the persisted
// session because tools load their state in useState initializers.
function getAuthSessionId(): string | null {
  try {
    const raw = localStorage.getItem(SUPABASE_STORAGE_KEYS.AUTH_TOKEN);
    if (!raw) return null;
    const session = JSON.parse(raw) as {
      access_token?: string;
      user?: { id?: string; last_sign_in_at?: string };
    };
    try {
      const payload = session.access_token?.split('.')[1] ?? '';
      const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { session_id?: string };
      if (claims.session_id) return claims.session_id;
    } catch {
      // Undecodable token; fall back to the user's sign-in time below.
    }
    return session.user?.id ? `${session.user.id}:${session.user.last_sign_in_at ?? ''}` : null;
  } catch {
    return null;
  }
}

// ---- IndexedDB ------------------------------------------------------------

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = () => request.result.createObjectStore(STORE_NAME);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    dbPromise.catch(() => { dbPromise = null; });
  }
  return dbPromise;
}

async function withStore<R>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<R>): Promise<R | undefined> {
  try {
    const db = await openDb();
    return await new Promise<R>((resolve, reject) => {
      const request = fn(db.transaction(STORE_NAME, mode).objectStore(STORE_NAME));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } catch {
    return undefined;
  }
}

const isFresh = (savedAt: number | undefined) => !!savedAt && Date.now() - savedAt <= MAX_AGE_MS;

// ---- Per-tool store -------------------------------------------------------

export interface ToolSessionStore<TSession, TForm> {
  loadSession(): TSession | null;
  saveSession(state: TSession): void;
  saveImages(files: File[]): Promise<void>;
  loadImages(): Promise<File[]>;
  saveHandoff(form: TForm, hasImages: boolean): Promise<void>;
  clearHandoff(): Promise<void>;
  takeHandoff(): Promise<{ form: TForm; images: File[] } | null>;
}

export function createToolSessionStore<TSession, TForm>(namespace: ToolNamespace): ToolSessionStore<TSession, TForm> {
  const sessionKey = `${namespace}-session`;
  const imagesKey = (tabId: string) => `${namespace}:images:${tabId}`;
  const handoffKey = `${namespace}:handoff`;

  async function readImages(tabId: string): Promise<File[]> {
    const record = await withStore<StoredImages | undefined>('readonly', store => store.get(imagesKey(tabId)));
    if (!record || !isFresh(record.savedAt)) return [];
    return record.files.map(f => new File([f.blob], f.name, { type: f.blob.type }));
  }

  async function writeImages(tabId: string, files: File[]): Promise<void> {
    if (files.length === 0) {
      await withStore('readwrite', store => store.delete(imagesKey(tabId)));
      return;
    }
    const record: StoredImages = { files: files.map(f => ({ name: f.name, blob: f })), savedAt: Date.now() };
    await withStore('readwrite', store => store.put(record, imagesKey(tabId)));
  }

  const clearHandoff = async () => {
    await withStore('readwrite', store => store.delete(handoffKey));
  };

  return {
    loadSession() {
      try {
        const raw = sessionStorage.getItem(sessionKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as SessionEnvelope<TSession>;
        if (parsed.version !== SESSION_VERSION) return null;
        // Written under a different login (or before one): start fresh.
        if (parsed.authSessionId !== getAuthSessionId()) return null;
        return parsed.state;
      } catch {
        return null;
      }
    },

    saveSession(state) {
      try {
        const envelope: SessionEnvelope<TSession> = { version: SESSION_VERSION, authSessionId: getAuthSessionId(), state };
        sessionStorage.setItem(sessionKey, JSON.stringify(envelope));
      } catch {
        // Storage full or unavailable; the page still works without persistence.
      }
    },

    saveImages: (files) => writeImages(getTabId(), files),
    loadImages: () => readImages(getTabId()),

    async saveHandoff(form, hasImages) {
      const record: StoredHandoff<TForm> = { form, imageTabId: hasImages ? getTabId() : null, savedAt: Date.now() };
      await withStore('readwrite', store => store.put(record, handoffKey));
    },

    clearHandoff,

    // Reads and removes the hand-off, moving its images under this tab's id.
    async takeHandoff() {
      const record = await withStore<StoredHandoff<TForm> | undefined>('readonly', store => store.get(handoffKey));
      await clearHandoff();
      if (!record || !isFresh(record.savedAt)) return null;

      const tabId = getTabId();
      let images: File[] = [];
      if (record.imageTabId) {
        images = await readImages(record.imageTabId);
        if (images.length > 0 && record.imageTabId !== tabId) {
          await writeImages(tabId, images);
          await writeImages(record.imageTabId, []);
        }
      }
      return { form: record.form, images };
    },
  };
}

// Database used before this store was shared between tools.
const LEGACY_DB_NAME = 'merchantfloor-listing-drafts';

// Drops records left behind by closed tabs.
export async function pruneExpiredDrafts(): Promise<void> {
  try {
    indexedDB.deleteDatabase(LEGACY_DB_NAME);
    const db = await openDb();
    const request = db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME).openCursor();
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) return;
      if (!isFresh((cursor.value as { savedAt?: number })?.savedAt)) cursor.delete();
      cursor.continue();
    };
  } catch {
    // Ignore; nothing to prune.
  }
}

// Called on sign-out so nothing is left behind on a shared computer.
export async function clearAllToolDrafts(): Promise<void> {
  try {
    TOOL_NAMESPACES.forEach(ns => sessionStorage.removeItem(`${ns}-session`));
  } catch {
    // Ignore
  }
  await withStore('readwrite', store => store.clear());
}
